import { useApolloClient } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';

import { BATCHED_GQL_RELATED_PRODUCTS } from '~/shared/constants/queryCacheKeys';
import useAssortmentTestConfigs from '~/shared/utils/useAssortmentTestConfigs';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';

import getGqlRelatedProductDataByGroupCode from '../api/getGqlRelatedProductDataByGroupCode';
import {
  BROWSE_API_PAGE_SIZE,
  FED_GRAPH_RELATED_PRODUCTS_STALE_TIME,
} from '../constants';

/**
 *
 * @description Hook to fetch product pricing data in batches
 *
 * Leverage the apollo client cache to only query for new batches of groupCodes.
 * This works well for paginated results where the groupCodes are expected to be in the same order for the same query params.
 * Apollo client will cache the previous pages (batches) of groupCodes and only fetch the latest page (batch) of groupCodes.
 *
 * Example for page size of 5:
 * 1st render: groupCodes = [1, 2, 3, 4, 5], fetches pricing for [1, 2, 3, 4, 5]
 * 2nd render: groupCodes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], fetches pricing for [6, 7, 8, 9, 10]
 * [1, 2, 3, 4, 5] are already cached in the apollo client cache and will not be fetched again unless the apollo cache is invalidated
 * (which currently only happens when the user refreshes the page or starts a new browser session)
 *
 * Assume we SPA navigate to a different page and come back to the original page:
 * 3rd render: groupCodes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], fetches pricing for [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
 * Both [1, 2, 3, 4, 5] and [6, 7, 8, 9, 10] batches are already cached in the apollo client cache and will not be fetched again unless the cache is invalidated
 *
 * If we need to bypass the apollo cache and always fetch the latest pricing data, we can use the fetchPolicy: 'network-only' option in the apollo client query
 * and set the staleTime to 0 in the useQuery options.
 *
 * Use the Apollo Client DevTools to inspect the cache and see the cached results for the productPricing query
 *
 * Caveat: If the order of groupCodes is expected to change, this hook will not work as expected.
 *
 * @param {Object} groupCodeObjects - array of objects { groupCode: string, masterProductIds: Array<string> }
 * @returns {Object}
 */
export const useBatchedGqlRelatedProducts = (groupCodeObjects = {}) => {
  const batches = [];
  for (let i = 0; i < groupCodeObjects.length; i += BROWSE_API_PAGE_SIZE) {
    batches.push(groupCodeObjects.slice(i, i + BROWSE_API_PAGE_SIZE));
  }

  const apolloClient = useApolloClient();
  const secondarySite = useSecondarySite();
  const { membershipLevelGroup, isVip } = useMembership();
  const { excludeTagIds } = useAssortmentTestConfigs();

  return useQuery({
    queryKey: [BATCHED_GQL_RELATED_PRODUCTS, groupCodeObjects, excludeTagIds], // Cache based on all requested groupCodeObjects and excludeTagIds
    queryFn: () =>
      Promise.all(
        batches.map((batchedGroupCodeObjects) =>
          getGqlRelatedProductDataByGroupCode({
            apolloClient,
            groupCodeObjects: batchedGroupCodeObjects,
            isWarehouseProduct: Boolean(secondarySite),
            membershipLevelGroup,
            isVip,
            excludeTagIds,
          })
        )
      ).then((results) =>
        results.flatMap((results) => {
          const productsByGroupCode =
            results.data?.productsByGroupCode?.productsByGroupCode || [];

          const mappedProducts = productsByGroupCode.map((group) => {
            const { products, ...rest } = group;
            const swatches = products.map((product) => product.swatch);

            const updatedProducts = products.map((product) => {
              const tagIdList = product.tags.map((tag) => tag.id);
              const finalDetails = product.pricing.discountDetails.promoPrice
                ? product.pricing.discountDetails
                : product.pricing.bogoDetails;

              return {
                ...product,
                isAvailable:
                  // master for most products, anyProfile for outfits
                  product.availableQuantity.master > 0 ||
                  product.availableQuantity.anyProfile > 0,
                tagIdList,
                pricing: {
                  ...product.pricing,
                  finalDetails,
                },
              };
            });

            return {
              ...rest,
              products: updatedProducts,
              swatches,
              label: products[0]?.label || '',
            };
          });
          return mappedProducts;
        })
      ),
    enabled: apolloClient && groupCodeObjects.length > 0, // Only fetch if there are groupCodeObjects to fetch
    staleTime: FED_GRAPH_RELATED_PRODUCTS_STALE_TIME, // when this stale time is reached, the apollo client cache will be the next layer
    keepPreviousData: true, // Keep previous data while fetching new data to prevent UI flickering from entering loading state while fetching new data
  });
};

export default useBatchedGqlRelatedProducts;
